import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import Dropzone from 'react-dropzone';
import Papa from 'papaparse';
import FileIcon from '../icons/FileIcon';
const CSVImport = ({ alias }) => {
    const [dragState, setDragState] = useState(false);
    const [uploadMessage, setUploadMessage] = useState(_jsx("span", {}));
    const [isUploading, setIsUploading] = useState(false);
    const optionalHeaders = ['first_name', 'last_name'];
    const anyOfRequired = ['email_address', 'phone_number'];
    const templateUrl = `${process.env.PUBLIC_URL || ''}/template.csv`;
    const validateCsvHeaders = (file) => new Promise((resolve) => {
        Papa.parse(file, {
            header: true,
            preview: 1,
            skipEmptyLines: true,
            transformHeader: (h) => (h || '').trim(),
            complete: (results) => {
                const fields = (results.meta && results.meta.fields) || [];
                const normalizedFields = fields.map((f) => (f || '').trim());
                const hasAnyRequired = anyOfRequired.some((h) => normalizedFields.includes(h));
                const missing = hasAnyRequired ? [] : anyOfRequired;
                resolve({ ok: hasAnyRequired, missing, fields: normalizedFields });
            },
            error: () => resolve({ ok: false, missing: anyOfRequired.slice(), fields: [] }),
        });
    });
    const handleDrop = async (acceptedFiles, _rejections) => {
        var _a;
        setDragState(false);
        if (!acceptedFiles || acceptedFiles.length === 0)
            return true;
        const file = acceptedFiles[0];
        // Validate headers before uploading
        setUploadMessage(_jsx("div", { className: "my-4 p-2 border border-wfGray-300", children: "Validating file\u2026" }));
        const validation = await validateCsvHeaders(file);
        if (!validation.ok) {
            setUploadMessage(_jsxs("div", { className: "my-4 p-2 border-2 border-salmon-600", children: [_jsx("strong", { children: "We couldn't find a required contact column." }), _jsxs("div", { className: "mt-1", children: ["Add either: ", anyOfRequired.join(' or ')] }), _jsxs("div", { className: "mt-1", children: ["Optional: ", optionalHeaders.join(', ')] }), _jsx("div", { className: "mt-2", children: "Please use the template or add the missing columns and try again." })] }));
            return true;
        }
        // mixpanel.track("Other Connect Completed")
        const formData = new FormData();
        formData.append("customers", file);
        let token = ((_a = document.querySelector('meta[name="withfriends:t"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || null;
        setIsUploading(true);
        setUploadMessage(_jsx("div", { className: "my-4 p-2 border border-wfGray-300", children: "Uploading\u2026" }));
        try {
            const response = await fetch(process.env.REACT_APP_RAILS_API + `/merchants/${alias}/customers/import`, {
                method: 'POST',
                body: formData,
                headers: [
                    ["Authorization", `Bearer ${token}`],
                ],
            });
            if (response.ok) {
                setUploadMessage(_jsx("div", { className: "my-4 p-2 border border-wfGray-300", children: "Success! We're processing your upload and will email you when it's done." }));
            }
            else {
                let msg = {};
                try {
                    msg = await response.json();
                }
                catch (_b) { }
                setUploadMessage(_jsxs("div", { className: "my-4 p-2 border-2 border-salmon-600", children: [_jsx("strong", { children: "Oh no! There was an error in your upload." }), _jsx("br", {}), " ", (msg === null || msg === void 0 ? void 0 : msg.error) || (msg === null || msg === void 0 ? void 0 : msg.message) || 'Check your template and try again, or email it to team@withfriends.co and we\'ll help you out.'] }));
            }
        }
        catch (_c) {
            setUploadMessage(_jsxs("div", { className: "my-4 p-2 border-2 border-salmon-600", children: [_jsx("strong", { children: "Oh no! There was an error in your upload." }), _jsx("br", {}), " Check your template and try again, or email it to team@withfriends.co and we'll help you out."] }));
        }
        finally {
            setIsUploading(false);
        }
        return true;
    };
    const handleDragOver = (ev) => {
        setDragState(true);
        ev.preventDefault();
    };
    const handleDragLeave = (ev) => {
        setDragState(false);
        ev.preventDefault();
    };
    const noDragClasses = "border-2 border-gray-400 border-dashed p-4 hover:border-gray-800 text-center cursor-pointer";
    const dragClasses = "bg-gray-200 border-gray-800 " + noDragClasses;
    return (_jsxs("div", { children: [uploadMessage, _jsx(Dropzone, { accept: { 'text/csv': ['.csv'] }, multiple: false, maxFiles: 1, disabled: isUploading, onDrop: handleDrop, onDropRejected: (rejections) => {
                    var _a, _b;
                    const reasons = (_b = (_a = rejections === null || rejections === void 0 ? void 0 : rejections[0]) === null || _a === void 0 ? void 0 : _a.errors) === null || _b === void 0 ? void 0 : _b.map((e) => e.message).join(', ');
                    setUploadMessage(_jsxs("div", { className: "my-4 p-2 border-2 border-salmon-600", children: [_jsx("strong", { children: "That file can\u2019t be used." }), _jsx("br", {}), " ", reasons || 'Please upload a .csv file using the template headers.'] }));
                }, onDragOver: handleDragOver, onDragLeave: handleDragLeave, children: ({ getRootProps, getInputProps }) => (_jsx("section", { className: dragState ? dragClasses : noDragClasses, children: _jsxs("div", Object.assign({}, getRootProps(), { children: [_jsx("input", Object.assign({}, getInputProps())), _jsx("span", { children: isUploading ? 'Uploading…' : 'Drag a CSV, or click to select a file' }), _jsx(FileIcon, { className: "h-12 w-12 fill-current mt-4 mx-auto text-gray-500" })] })) })) }), _jsxs("div", { className: "mb-2 text-center", children: [_jsxs("div", { className: "text-sm text-gray-600 mt-1", children: ["Required: ", anyOfRequired.join(' or '), "; Optional: ", optionalHeaders.join(', ')] }), _jsx("a", { href: templateUrl, className: "text-sm underline", download: true, children: "Download the CSV template" })] })] }));
};
export default CSVImport;
